import React, { PureComponent, ReactNode } from 'react';

import { VenueTagData } from 'js/components/VenueTag/VenueTag';
import { EyecatcherType } from 'js/pages/BrowsePage/BrowseResult/Eyecatcher';
import {
  Stack,
  Box,
  Text,
  Inline,
  IconChevronRight,
  storage,
} from '@treatwell/ui';
import { BrowseMapPopup } from 'js/components/MapPopup';
import { isModifiedClick } from 'js/helpers/modified-click';
import { ImageOutput } from 'js/model/rainbow/ImageOutput';
import { VenueRating } from 'js/model/rainbow/venue/VenueOutput';
import { ChannelOutput } from 'js/model/rainbow/content/ChannelOutput';
import { CmsBrowsePage } from 'js/model/cms/cms-browse-page';
import { PricingDisplayType } from 'js/model/rainbow/venue/VenueMenuItemOutput';
import { PriceRangeOutput } from 'js/model/rainbow/PriceRangeOutput';
import { DurationRange } from 'js/model/rainbow/venue/TreatmentVenueMenuItemOutput';
import { AccoladeOutput } from 'js/model/rainbow/browse-page/AccoladeOutput';
import { Image } from 'src/js/model/rainbow/ImageOutput';
import clsx from 'clsx';
import { t } from 'i18next';
import { Category } from '../tracking/tracking';
import {
  bttBrowseCardToVenuePage,
  browseCardToVenuePage,
} from '../tracking/browse-result';
import { BrowseResultQuickview } from './BrowseResultQuickview';
import { BrowseResultService } from './BrowseResultService';
import { BrowseResultSummary } from './BrowseResultSummary';
import styles from './BrowseResult.module.css';
import { trackVenueResultData } from './tracking';

interface Props {
  name: string;
  shortAddress: string;
  openingHours: BrowseResultQuickview['props']['openingHours'];
  distance?: string;
  rating: VenueRating;
  openMapText: string;
  images: ImageOutput[];
  eyecatcher?: EyecatcherType;
  venuePageUri: string;
  venueId: number;
  tags: VenueTagData[];
  menuHighlights: MenuHighlight[];
  description?: string;
  quickviewLabels: Pick<
    BrowseResultQuickview['props'],
    'openLabel' | 'closeLabel' | 'buttonLabel'
  >;
  mapPopup: {
    mapApiKey: string;
    geoCode: { lat: number; lon: number };
    mapCenter: {
      zoom: number;
      lat: number;
      lon: number;
    };
    addressLines: string[];
    ctaText: string;
  };
  bestTreatmentsBadgeLabel?: string;
  bestTreatmentsBadgeTooltip: string;
  bestTreatmentsTrackingIds: number[];
  reviewCountTemplate: string;
  channelOutput: ChannelOutput;
  cmsBrowsePage: CmsBrowsePage;
  onVenueLeave?: (venueId: number) => void;
  onVenueEnter?: (venueId: number, rating: number) => void;
  isInHybridView?: boolean;
  isLazy?: boolean;
  venueRank?: number;
  isNewVenue?: boolean;
  accolades?: AccoladeOutput[];
  isPinned?: boolean;
}

interface State {
  isMapPopupShown: boolean;
  isQuickViewOpen: boolean;
}

export interface MenuHighlight {
  dataOfferId: string;
  name: string;
  duration?: string;
  numGuests?: number;
  priceRangeOutput: PriceRangeOutput;
  pricingDisplayType: PricingDisplayType;
  durationRange: DurationRange;
}

export class BrowseResult extends PureComponent<Props, State> {
  public state: State = {
    isMapPopupShown: false,
    isQuickViewOpen: false,
  };

  private onVenueEnter = () => {
    this.props.onVenueEnter?.(this.props.venueId, this.props.rating.average);
  };

  private onVenueLeave = () => {
    this.props.onVenueLeave?.(this.props.venueId);
  };

  private onLocationClick = (event: React.MouseEvent): void => {
    event.preventDefault();
    trackVenueResultData({
      eventName: 'search_show_on_map_clicked',
      menuHighlights: this.props.menuHighlights,
      venueId: this.props.venueId,
      rating: this.props.rating,
      tags: this.props.tags,
      countryCode: this.props.channelOutput.country.countryCode,
      venueRank: this.props.venueRank,
      isNewVenue: this.props.isNewVenue,
    });

    this.setState({ isMapPopupShown: true });
  };

  private onMapPopupCloseRequest = (): void => {
    this.setState({ isMapPopupShown: false });
  };

  private goToVenue = async (event: React.MouseEvent): Promise<void> => {
    if (event.defaultPrevented) {
      // The click is for some element that is a descendant of the <a> that
      // is processed in some other way. For example opening a map popup.
      return;
    }

    if (isModifiedClick(event)) {
      // It's not a regular click. Typically it's a middle click or ctrl+click.
      // Some browser call a click listener for a middle click, and some do not.
      // Allow the browser to handle it in the manner that the user expects.
      //
      // If the user is opening the venue page in another tab or window the
      // bttBrowseCardToVenuePage call for tracking will be skipped.
      // That's unfortunate, but fighting it leads to cross browser inconsistencies.
      return;
    }

    event.preventDefault();

    if (this.props.isPinned) {
      storage.session.setItem('pinned_venue', this.props.venueId);
    }

    const category = this.props.isInHybridView
      ? Category.BrowseHybridCard
      : Category.BrowseListCard;

    await Promise.all(
      this.props.bestTreatmentsTrackingIds.map((trackingId) =>
        bttBrowseCardToVenuePage(
          category,
          this.props.venueId.toString(),
          trackingId.toString()
        )
      )
    );

    const featuredAccolade = this.props.accolades?.find(
      (accolade) => accolade.featured
    );

    await trackVenueResultData({
      eventName: 'search_venue_clicked',
      menuHighlights: this.props.menuHighlights,
      venueId: this.props.venueId,
      rating: this.props.rating,
      tags: this.props.tags,
      countryCode: this.props.channelOutput.country.countryCode,
      venueRank: this.props.venueRank,
      isNewVenue: this.props.isNewVenue,
      displayedAccolade: featuredAccolade?.name,
      isVenueCardExpanded: this.state.isQuickViewOpen,
      pageName: 'browse_page',
      venueCardType: 'list',
    });

    browseCardToVenuePage(category, this.props.venueId.toString());

    window.location.assign(this.props.venuePageUri);
  };

  private getImages(images: ImageOutput[]): Image[] {
    return images.map((image) => image.uris);
  }

  public render(): ReactNode {
    const mapVenue = {
      id: this.props.venueId,
      location: this.props.mapPopup.geoCode,
      rating: this.props.rating,
    };

    const { menuHighlights, channelOutput, cmsBrowsePage } = this.props;

    return (
      <>
        <div
          className={clsx(styles.container, {
            [styles.isPinned]: this.props.isPinned,
          })}
          onMouseLeave={this.onVenueLeave}
          onMouseEnter={this.onVenueEnter}
        >
          <Box paddingWide="none" padding="none">
            <a href={this.props.venuePageUri} onClick={this.goToVenue}>
              {this.props.isPinned && (
                <Inline className={styles.pinnedCardHeader} justify="between">
                  <Text type="footnoteHeavy">
                    {t<string>('browsePage.pinnedVenueCardText')}
                  </Text>
                  <IconChevronRight size={16} />
                </Inline>
              )}

              <Stack space="sm">
                <BrowseResultSummary
                  id={this.props.venueId}
                  name={this.props.name}
                  address={this.props.shortAddress}
                  distance={this.props.distance}
                  rating={this.props.rating}
                  reviewCountTemplate={this.props.reviewCountTemplate}
                  openMapText={this.props.openMapText}
                  images={this.getImages(this.props.images)}
                  eyecatcher={this.props.eyecatcher}
                  tags={this.props.tags}
                  onLocationClick={this.onLocationClick}
                  bestTreatmentsBadgeLabel={this.props.bestTreatmentsBadgeLabel}
                  bestTreatmentsBadgeTooltip={
                    this.props.bestTreatmentsBadgeTooltip
                  }
                  bestTreatmentsTrackingIds={
                    this.props.bestTreatmentsTrackingIds
                  }
                  isInHybridView={this.props.isInHybridView}
                  isLazy={this.props.isLazy ?? true}
                />

                {!this.props.isPinned && (
                  <>
                    <div className={styles.divider} />
                    <Stack space="sm">
                      {menuHighlights.map((menuHighlight) => (
                        <BrowseResultService
                          key={menuHighlight.dataOfferId}
                          dataOfferId={menuHighlight.dataOfferId}
                          name={menuHighlight.name}
                          duration={menuHighlight.duration}
                          numGuests={menuHighlight.numGuests}
                          priceRangeOutput={menuHighlight.priceRangeOutput}
                          cmsBrowsePage={cmsBrowsePage}
                          channelOutput={channelOutput}
                          pricingDisplayType={menuHighlight.pricingDisplayType}
                        />
                      ))}
                    </Stack>
                  </>
                )}

                <div className={styles.divider} />
                <BrowseResultQuickview
                  description={this.props.description}
                  openingHours={this.props.openingHours}
                  onGoToVenueButtonClick={this.goToVenue}
                  {...this.props.quickviewLabels}
                  isInHybridView={this.props.isInHybridView}
                  menuHighlights={menuHighlights}
                  venueId={this.props.venueId}
                  rating={this.props.rating}
                  tags={this.props.tags}
                  venueRank={this.props.venueRank}
                  countryCode={channelOutput.country.countryCode}
                  isNewVenue={this.props.isNewVenue}
                  onQuickviewTabClick={(isOpen) =>
                    this.setState({ isQuickViewOpen: isOpen })
                  }
                />
              </Stack>
            </a>
          </Box>
        </div>
        {this.state.isMapPopupShown && (
          <BrowseMapPopup
            mapCenter={this.props.mapPopup.mapCenter}
            name={this.props.name}
            rating={this.props.rating}
            reviewCountText={this.props.reviewCountTemplate}
            addressLines={this.props.mapPopup.addressLines}
            goToVenueCta={{
              label: this.props.mapPopup.ctaText,
              url: this.props.venuePageUri,
              onClick: this.goToVenue,
              venueId: this.props.venueId.toString(),
            }}
            trackEventCategory={
              this.props.isInHybridView
                ? Category.BrowseHybridCard
                : Category.BrowseListCard
            }
            onMapPopupCloseRequest={this.onMapPopupCloseRequest}
            mapVenue={mapVenue}
          />
        )}
      </>
    );
  }
}
